/**
 * @fileoverview Which students have gone quiet (F3).
 *
 * The single most actionable thing Hans can compute for an instructor: which
 * of their students has not touched the instrument since the last lesson. It
 * is the one datum they have no other way to get — practice happens between
 * lessons, out of sight, and by the time it shows up in a lesson the week is
 * already lost.
 *
 * Two shaping decisions. It leads with the students who need attention, not
 * with the roster — sorting by name would bury the three people the card
 * exists to surface. And a student with no portal account is shown as an
 * INVITE rather than a worry: they have not stopped practising, they have
 * never been asked, and that difference is the difference between a problem
 * and an opportunity.
 */

import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { practiceLogsEnabled } from "@/lib/featureFlags";
import { Card, SectionHeader, ListRow } from "@/components/studio/primitives";

function quietLabel(row) {
  if (!row.linked) return "Not on the portal yet";
  if (row.days_since_practice === null || row.days_since_practice === undefined) return "No practice logged";
  if (row.days_since_practice === 0) return "Practised today";
  if (row.days_since_practice === 1) return "Practised yesterday";
  return `${row.days_since_practice} days since practice`;
}

export function RosterHealthCard() {
  const { user } = useContext(AuthContext) || {};
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  // Practice health reads the manual practice log, so it goes dark with it.
  // The 404 alone would hide the card (rows stays null), but skipping a fetch
  // we know will fail keeps a guaranteed-404 request off every dashboard load.
  // Absent field ⇒ enabled, so a payload predating the flag still shows it.
  const enabled = practiceLogsEnabled(user);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    api
      .get("/roster/health")
      .then(({ data }) => alive && setRows(data.students || []))
      .catch(() => {
        /* A dashboard card is not worth an error toast. */
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  if (!enabled) return null;
  if (!rows || rows.length === 0) return null;

  const needsAttention = rows.filter((r) => r.needs_attention);
  const uninvited = rows.filter((r) => !r.linked);

  return (
    <Card>
      <SectionHeader>Practice health</SectionHeader>

      {needsAttention.length === 0 ? (
        <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid="roster-health-all-well">
          Everyone on the portal has practised in the last week.
        </p>
      ) : (
        <>
          <p className="mb-3 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="roster-health-count">
            {needsAttention.length} student{needsAttention.length === 1 ? "" : "s"} haven&apos;t practised in a while.
          </p>
          <div className="-mx-[18px]">
            {needsAttention.slice(0, 3).map((row, i) => (
              <ListRow
                key={row.student_id}
                title={row.name}
                subtitle={quietLabel(row)}
                borderBottom={i < Math.min(3, needsAttention.length) - 1}
                // The instructor's own student page — `/student/*` is the
                // student portal, which an instructor session cannot use.
                onPress={() => navigate(`/students/${row.student_id}`)}
              />
            ))}
          </div>
        </>
      )}

      {uninvited.length > 0 ? (
        <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="roster-health-uninvited">
          {uninvited.length} student{uninvited.length === 1 ? " isn't" : "s aren't"} on the portal yet — you&apos;ll see
          their practice once they are.{" "}
          <Link to="/students" className="text-[var(--accent-yellow-text)]">
            Invite them
          </Link>
        </p>
      ) : null}
    </Card>
  );
}

export default RosterHealthCard;
